import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { DateRangePicker } from "@/components/DateRangePicker";
import { LoadingComponent } from "@/components/LoadingComponent";
import { DataPoint, MultiLine } from "@/components/SparkSpreadDashboard/MultiLine";
import {
  SparkSpreadFuturesContract,
  SparkSpreadFuturesContractSelect,
} from "@/components/SparkSpreadDashboard/SparkSpreadFuturesContractSelect";
import {
  SparkSpreadFuturesContractType,
  SparkSpreadFuturesContractTypesSelect,
} from "@/components/SparkSpreadDashboard/SparkSpreadFuturesContractTypesSelect";
import {
  SparkSpreadProduct,
  SparkSpreadProductSelect,
} from "@/components/SparkSpreadDashboard/SparkSpreadProductSelect";
import {
  getUniqueContractTypes,
  transformSettlementsToMultiChartData,
  transformSpspQueryToMultiChartData,
} from "@/components/SparkSpreadDashboard/utils";
import { ListOfPriceItems } from "@/client/index";

export function SparkSpreadDashboardView({
  queryKey,
  queryFn,
}: {
  queryKey: string[];
  queryFn: () => Promise<ListOfPriceItems>;
}) {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: new Date("2022-11-16"),
    to: new Date(),
  });
  const [selectedSpread, setSelectedSpread] = useState<SparkSpreadProduct>("SpSp Base (5/2)");
  const [selectedContractType, setSelectedContractType] = useState<SparkSpreadFuturesContractType[]>(["Year"]);
  const [selectedContracts, setSelectedContracts] = useState<SparkSpreadFuturesContract[]>([]);

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn,
  });

  const multiLineSpSpData = useMemo(() => {
    if (!data) {
      return [];
    }
    const transformedData = transformSpspQueryToMultiChartData({
      data: data,
      dateRange,
      selectedSpread,
      selectedContracts,
    });
    if (!transformedData[selectedSpread] || transformedData[selectedSpread].length === 0) {
      return [];
    }
    return transformedData[selectedSpread] as DataPoint[];
  }, [data, selectedSpread, selectedContracts, dateRange]);

  const multiLineTTFData = useMemo(() => {
    if (!data) {
      return [];
    }
    const transformedData = transformSettlementsToMultiChartData({
      data: data,
      dateRange,
      selectedContracts,
      product: "TTF",
    });
    return transformedData;
  }, [data, selectedContracts, dateRange]);

  const multiLineBaseNL = useMemo(() => {
    if (!data) {
      return [];
    }
    return transformSettlementsToMultiChartData({
      data: data,
      dateRange,
      selectedContracts,
      product: "Base NL",
    });
  }, [data, selectedContracts, dateRange]);

  const multiLinePeakNL = useMemo(() => {
    if (!data) {
      return [];
    }
    return transformSettlementsToMultiChartData({
      data: data,
      dateRange,
      selectedContracts,
      product: "Peak NL",
    });
  }, [data, selectedContracts, dateRange]);

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return (
      <div>
        {t("Error loading data")}: {error.message}
      </div>
    );
  }

  if (!data?.data) {
    return <div>{t("No data available")}</div>;
  }

  return (
    <div className="container flex flex-col gap-4 p-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <DateRangePicker date={dateRange} setDate={setDateRange} className="w-full" />
          <SparkSpreadProductSelect selectedSpread={selectedSpread} setSelectedSpread={setSelectedSpread} />
        </div>
        <div className="flex flex-col gap-2">
          <SparkSpreadFuturesContractTypesSelect
            selectedContractTypes={selectedContractType}
            setSelectedContractTypes={setSelectedContractType}
          />
          <SparkSpreadFuturesContractSelect
            uniquePeriods={getUniqueContractTypes({ contractType: selectedContractType, data, dateRange })}
            selectedPeriods={selectedContracts}
            setSelectedPeriods={setSelectedContracts}
          />
        </div>
      </div>
      {multiLineSpSpData.length > 0 ? (
        <div className="flex flex-col gap-4">
          <div className="rounded-xl bg-muted/50">
            <MultiLine
              data={multiLineSpSpData}
              title={`${t("Sparkspread")} ${selectedSpread}`}
              textCenter
              referenceLineAt0
              showSettlementAsDotted={true}
            />
          </div>
          <div className="flex w-full flex-col justify-between gap-2 sm:flex-row">
            {multiLineTTFData.length > 0 && (
              <div className="rounded-xl bg-muted/50">
                <MultiLine data={multiLineTTFData} title={t("Supporting plot: TTF")} />
              </div>
            )}
            {multiLineBaseNL.length > 0 && (
              <div className="rounded-xl bg-muted/50">
                <MultiLine data={multiLineBaseNL} title={t("Supporting plot: Base NL")} />
              </div>
            )}
            {multiLinePeakNL.length > 0 && (
              <div className="rounded-xl bg-muted/50">
                <MultiLine data={multiLinePeakNL} title={t("Supporting plot: Peak NL")} />
              </div>
            )}
          </div>
        </div>
      ) : selectedContracts.length === 0 ? (
        <div className="text-center">{t("Please select a contract")}</div>
      ) : (
        <div>{t("No data available for the selected spread and contracts")}</div>
      )}
    </div>
  );
}
